import type { HistoryEvent } from '../types/workflow-inspection.types';

/**
 * Merge freshly fetched/streamed events into what's already held: de-dupe by
 * `eventId`, keep ascending order, and back-fill `activityId`/`activityType`
 * on activity events from their `ActivityTaskScheduled` event (the server only
 * fills those when both land in the same response, which paging and the live
 * tail routinely break).
 */
export function mergeHistoryEvents(
  existing: HistoryEvent[],
  incoming: HistoryEvent[],
): HistoryEvent[] {
  if (incoming.length === 0) return existing;
  const byId = new Map<number, HistoryEvent>();
  for (const e of existing) byId.set(e.eventId, e);
  for (const e of incoming) byId.set(e.eventId, e);
  const sorted = [...byId.values()].sort((a, b) => a.eventId - b.eventId);
  return backfillActivityRefs(sorted);
}

/** Copy activity id/type from each Scheduled event onto the events that reference it. */
function backfillActivityRefs(events: HistoryEvent[]): HistoryEvent[] {
  const scheduled = new Map<number, HistoryEvent>();
  for (const e of events) {
    if (e.eventType === 'ActivityTaskScheduled') scheduled.set(e.eventId, e);
  }
  return events.map((e) => {
    if (e.scheduledEventId === null || (e.activityId && e.activityType)) return e;
    const source = scheduled.get(e.scheduledEventId);
    if (!source) return e;
    return {
      ...e,
      activityId: e.activityId ?? source.activityId,
      activityType: e.activityType ?? source.activityType,
    };
  });
}

/** Highest eventId held, or null when nothing is loaded yet (the live tail's cursor). */
export function lastEventId(events: HistoryEvent[]): number | null {
  return events.length === 0 ? null : (events[events.length - 1]?.eventId ?? null);
}
